<script setup lang="ts">
    import type { AccessGroupNode } from '@/js/services/api/role/types';
    import { OrgLevelType } from '@/js/services/api/role/types';
    import ToggleSwitch from '../ToggleSwitch.vue';
    import EchoTooltip from '../EchoTooltip.vue';
    import { CheckIcon, ChevronRightIcon, CirclePlusIcon, CircleMinusIcon } from '@lucide/vue';
    import { __ } from '@/js/stores/expressions';
    import { ref, computed } from 'vue';

    const props = defineProps<{
        nodes: AccessGroupNode[];
        disabled?: boolean;
        lockedSelected?: boolean;
        depth?: number;
        // Flat role-baseline lookup (keyed by node id, shared across all depths).
        // When omitted (role editor) no override badges render — unchanged behavior.
        baselineById?: Map<string, AccessGroupNode>;
    }>();

    type FlagDiff = 'added' | 'removed' | null;

    // Compare a node against its role baseline on the three access booleans.
    // Mirrors ModuleAccessControl.flagDiffState: 'added' = the user grants what
    // the role lacks, 'removed' = the user revokes what the role grants.
    const nodeDiff = (node: AccessGroupNode): FlagDiff => {
        if (!props.baselineById) return null;
        const base = props.baselineById.get(node.id);
        const baseSelected = base?.isSelected ?? false;
        const baseIndividuals = base?.includeAllIndividuals ?? false;
        const baseChildGroups = base?.includeAllChildGroups ?? false;

        const grants =
            (node.isSelected && !baseSelected) ||
            (node.includeAllIndividuals && !baseIndividuals) ||
            (node.includeAllChildGroups && !baseChildGroups);
        const revokes =
            (!node.isSelected && baseSelected) ||
            (!node.includeAllIndividuals && baseIndividuals) ||
            (!node.includeAllChildGroups && baseChildGroups);

        // A node toggles several flags, so it can in principle grant one while
        // revoking another. Only badge when the direction is unambiguous.
        if (grants && revokes) return null;
        if (grants) return 'added';
        if (revokes) return 'removed';
        return null;
    };

    const emit = defineEmits<{
        (e: 'update:nodes', nodes: AccessGroupNode[]): void;
    }>();

    const currentDepth = props.depth ?? 0;

    const isGroupOrTeam = (node: AccessGroupNode) => node.orgLevelType !== OrgLevelType.Individual;

    const expanded = ref<Record<string, boolean>>(
        Object.fromEntries(props.nodes.filter(isGroupOrTeam).map((n) => [n.id, false]))
    );

    const toggleExpanded = (nodeId: string) => {
        expanded.value[nodeId] = !expanded.value[nodeId];
    };

    const toggleSelected = (index: number) => {
        const updated = [...props.nodes];
        const node = updated[index];
        // Deselecting a parent cascades down: clears the include-all flags and
        // deselects every descendant individual, so no individual stays granted
        // under a parent the user just revoked.
        updated[index] = node.isSelected
            ? setGroupsSelectedRecursive(node, false)
            : { ...node, isSelected: true };
        emit('update:nodes', updated);
    };

    const setSelectedRecursive = (node: AccessGroupNode, selected: boolean): AccessGroupNode => ({
        ...node,
        isSelected: selected,
        includeAllIndividuals: selected ? node.includeAllIndividuals : false,
        children: node.children.map((child) => setSelectedRecursive(child, selected)),
    });

    const updateIncludeAllIndividuals = (index: number, value: boolean) => {
        const updated = [...props.nodes];
        const node = updated[index];
        updated[index] = {
            ...node,
            includeAllIndividuals: value,
            children: node.children.map((child) => setSelectedRecursive(child, value)),
        };
        emit('update:nodes', updated);
    };

    const includesAllIndividuals = (node: AccessGroupNode): boolean => {
        return node.orgLevelType === OrgLevelType.Team && node.includeAllIndividuals;
    };

    const includesAllChildGroups = (node: AccessGroupNode): boolean => {
        return node.orgLevelType === OrgLevelType.Group && node.includeAllChildGroups;
    };

    const updateIncludeAllChildGroups = (index: number, value: boolean) => {
        const updated = [...props.nodes];
        const node = updated[index];
        updated[index] = {
            ...node,
            includeAllChildGroups: value,
            children: node.children.map((child) =>
                child.orgLevelType === OrgLevelType.Individual
                    ? child
                    : value
                      ? { ...child, isSelected: true }
                      : setGroupsSelectedRecursive(child, false)
            ),
        };
        emit('update:nodes', updated);
    };

    // The master checkbox spans the whole subtree, not just the visible top
    // level, so both predicates recurse. Individuals are excluded: toggleAll
    // never selects them, so counting them would keep the box from ever ticking.
    const everyGroupOrTeamSelected = (nodes: AccessGroupNode[]): boolean =>
        nodes.every(
            (n) =>
                n.orgLevelType === OrgLevelType.Individual ||
                (n.isSelected && everyGroupOrTeamSelected(n.children))
        );
    const someGroupOrTeamSelected = (nodes: AccessGroupNode[]): boolean =>
        nodes.some(
            (n) =>
                n.orgLevelType !== OrgLevelType.Individual &&
                (n.isSelected || someGroupOrTeamSelected(n.children))
        );

    const allSelected = computed(() => everyGroupOrTeamSelected(props.nodes));
    const someSelected = computed(() => someGroupOrTeamSelected(props.nodes));

    const setGroupsSelectedRecursive = (
        node: AccessGroupNode,
        selected: boolean
    ): AccessGroupNode => {
        if (node.orgLevelType === OrgLevelType.Individual) {
            return selected ? node : { ...node, isSelected: false };
        }
        return {
            ...node,
            isSelected: selected,
            includeAllIndividuals: selected ? node.includeAllIndividuals : false,
            includeAllChildGroups: selected ? node.includeAllChildGroups : false,
            children: node.children.map((child) => setGroupsSelectedRecursive(child, selected)),
        };
    };

    const toggleAll = () => {
        const newSelected = !allSelected.value;
        emit(
            'update:nodes',
            props.nodes.map((n) => setGroupsSelectedRecursive(n, newSelected))
        );
    };

    const updateChildren = (index: number, children: AccessGroupNode[]) => {
        const updated = [...props.nodes];
        const node = updated[index];
        updated[index] = {
            ...node,
            children,
        };
        emit('update:nodes', updated);
    };
</script>

<template>
    <div class="flex flex-col" :class="{ '-ml-7.5': currentDepth === 0 }">
        <div v-if="currentDepth === 0" class="flex items-center gap-1.5 py-1.5">
            <span class="w-6 shrink-0" />
            <label class="flex items-start py-1 text-body">
                <input
                    type="checkbox"
                    class="Custom-checkbox"
                    :checked="allSelected"
                    :indeterminate.prop="!allSelected && someSelected"
                    :disabled="disabled"
                    :aria-label="__('org-levels')"
                    @change="toggleAll"
                />
                {{ __('org-levels') }}
            </label>
            <div class="ml-4">
                <slot name="root-actions" />
            </div>
        </div>
        <slot v-if="currentDepth === 0" name="description" />
        <div v-for="(node, index) in nodes" :key="node.id">
            <div
                class="flex items-center gap-1.5 py-1.5 relative"
                :style="{ paddingLeft: `${(currentDepth + 1) * 1.8}rem` }"
            >
                <button
                    v-if="node.children.length"
                    type="button"
                    class="shrink-0 rounded cursor-pointer self-start custom-focus"
                    :aria-label="expanded[node.id] ? __('collapse') : __('expand')"
                    @click="toggleExpanded(node.id)"
                >
                    <ChevronRightIcon
                        class="size-6 transition-transform duration-150"
                        :class="{ '-rotate-90': expanded[node.id] }"
                    />
                </button>
                <span v-else class="w-6 shrink-0" />
                <label class="flex items-start py-1 text-body">
                    <input
                        type="checkbox"
                        class="Custom-checkbox"
                        :checked="node.isSelected"
                        :disabled="
                            disabled ||
                            lockedSelected ||
                            (node.orgLevelType === OrgLevelType.Team &&
                                node.includeAllIndividuals) ||
                            (node.orgLevelType === OrgLevelType.Group &&
                                node.includeAllChildGroups) ||
                            node.orgLevelType === OrgLevelType.Individual
                        "
                        :aria-label="node.name"
                        @change="toggleSelected(index)"
                    />
                    {{ node.name }}
                </label>
                <EchoTooltip
                    v-if="nodeDiff(node) === 'added'"
                    :label="__('user-org-access-added')"
                    class="ml-2 inline-flex"
                    tabindex="0"
                >
                    <CirclePlusIcon class="size-4 text-primary-carrot" />
                </EchoTooltip>
                <EchoTooltip
                    v-else-if="nodeDiff(node) === 'removed'"
                    :label="__('user-org-access-removed')"
                    class="ml-2 inline-flex"
                    tabindex="0"
                >
                    <CircleMinusIcon class="size-4 text-primary-carrot" />
                </EchoTooltip>
                <ToggleSwitch
                    v-if="node.isSelected && node.orgLevelType === OrgLevelType.Group"
                    :model-value="node.includeAllChildGroups ?? false"
                    :label="__('include-all-child-groups')"
                    :disabled="disabled"
                    class="ml-4 -my-1"
                    @update:model-value="updateIncludeAllChildGroups(index, $event as boolean)"
                />
                <span
                    v-if="
                        node.isSelected &&
                        node.orgLevelType === OrgLevelType.Group &&
                        node.includeAllChildGroups
                    "
                    class="flex gap-1 text-sm items-center ml-2"
                >
                    <CheckIcon class="size-5" />
                    {{
                        (() => {
                            const count = node.children.filter(
                                (c) => c.orgLevelType !== OrgLevelType.Individual
                            ).length;
                            return __(count === 1 ? 'child-group-count-one' : 'child-group-count', {
                                count: String(count),
                            });
                        })()
                    }}
                </span>
                <ToggleSwitch
                    v-if="node.isSelected && node.orgLevelType === OrgLevelType.Team"
                    :model-value="node.includeAllIndividuals ?? false"
                    :label="__('include-all-individuals')"
                    :disabled="disabled"
                    class="ml-4 -my-1"
                    @update:model-value="updateIncludeAllIndividuals(index, $event as boolean)"
                />

                <span
                    v-if="
                        node.isSelected &&
                        node.orgLevelType === OrgLevelType.Team &&
                        node.includeAllIndividuals
                    "
                    class="flex gap-1 text-sm items-center ml-2"
                >
                    <CheckIcon class="size-5" />
                    {{
                        __(
                            node.children.length === 1
                                ? 'individual-level-agent-count-one'
                                : 'individual-level-agent-count',
                            { count: String(node.children.length) }
                        )
                    }}
                </span>
            </div>

            <template v-if="isGroupOrTeam(node) && expanded[node.id]">
                <OrgDataAccessTree
                    v-if="node.children.length"
                    :nodes="node.children"
                    :disabled="disabled || includesAllIndividuals(node)"
                    :locked-selected="includesAllChildGroups(node)"
                    :depth="currentDepth + 1"
                    :baseline-by-id="baselineById"
                    @update:nodes="updateChildren(index, $event)"
                />
            </template>
        </div>
    </div>
</template>
